import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "./api";

export interface Account {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  status: "active" | "archived";
  role: "owner" | "admin" | "developer" | "mailbox_user";
}

interface AccountContextValue {
  currentAccount: Account;
  accounts: Account[];
  switchAccount: (id: string) => void;
  createAccount: (input: { name: string; slug: string; description?: string }) => Promise<Account>;
  refreshAccounts: () => Promise<unknown>;
}

const STORAGE_KEY = "cfmailos:selected-account";
const AccountContext = createContext<AccountContextValue | null>(null);

export function AccountProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const [selectedId, setSelectedId] = useState(() => localStorage.getItem(STORAGE_KEY));
  const query = useQuery({ queryKey: ["accounts"], queryFn: () => api<{ data: Account[] }>("/api/accounts") });
  const accounts = useMemo(() => query.data?.data ?? [], [query.data]);
  const currentAccount = accounts.find((account) => account.id === selectedId) ?? accounts.find((account) => account.status === "active") ?? accounts[0];

  useEffect(() => {
    if (!currentAccount || currentAccount.id === selectedId) return;
    localStorage.setItem(STORAGE_KEY, currentAccount.id);
    setSelectedId(currentAccount.id);
  }, [currentAccount, selectedId]);

  if (query.isLoading) return <div className="splash"><strong>Loading Accounts…</strong><span className="loader-line"/></div>;
  if (query.error) throw query.error;
  if (!currentAccount) throw new Error("No Account is available. Sign in as the configured instance owner to create one.");
  if (currentAccount.id !== selectedId) return <div className="splash"><strong>Selecting Account…</strong><span className="loader-line"/></div>;

  function switchAccount(id: string) {
    if (!accounts.some((account) => account.id === id)) return;
    localStorage.setItem(STORAGE_KEY, id);
    setSelectedId(id);
  }

  async function createAccount(input: { name: string; slug: string; description?: string }): Promise<Account> {
    const result = await api<{ data: Account }>("/api/accounts", { method: "POST", body: JSON.stringify(input) });
    client.setQueryData<{ data: Account[] }>(["accounts"], (current) => ({
      data: [...(current?.data.filter((account) => account.id !== result.data.id) ?? []), result.data]
        .sort((a, b) => a.name.localeCompare(b.name)),
    }));
    localStorage.setItem(STORAGE_KEY, result.data.id);
    setSelectedId(result.data.id);
    await client.invalidateQueries({ queryKey: ["accounts"] });
    return result.data;
  }

  return <AccountContext.Provider value={{ currentAccount, accounts, switchAccount, createAccount, refreshAccounts: () => client.invalidateQueries({ queryKey: ["accounts"] }) }}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountContextValue {
  const value = useContext(AccountContext);
  if (!value) throw new Error("useAccount must be used inside AccountProvider");
  return value;
}
